import { observable, configure, action, computed, runInAction } from 'mobx';

import util from 'preact-util';
import { route } from 'preact-router';
import PubSub, { topics } from '../lib/pubsub';

configure({ enforceActions: 'always' });

class FikenStore {
    constructor() {

    }

    @observable slug = ''; // Demo kunde
    @observable contactId = ''; // Demo kunde
    @observable contactPersonId = ''; // Demo kunde
    @observable invoiceId = ''; // Demo kunde

    @action
    updateKeyValue(key, value) {
        this[key] = value;
    }

    @action
    updateObjectKeyValue(obj, key, value) {
        this[obj][key] = value;
    }

    @observable redirectUrl = null;

    @observable oauthStatus = null;
    @observable oauthError = null;

    @action
    setOAuthStatus(status) {
        this.oauthStatus = status;
    }

    @action
    setOAuthError(error) {
        this.oauthError = error;
    }

    /**
     * Initiate OAuth connection flow
     * Redirects user to Fiken authorization page
     */
    async connectOAuth(customerId) {
        // Get JWT token using the proper utility method
        const token = util.getJwtToken();
        const apiServer = util.getApiServer();

        if (token) {
            // Pass token as query parameter for authentication
            // Note: JWT middleware expects the parameter to be named 'jwtToken'
            window.location.href = `${apiServer}/api/integrations/fiken/request-access?jwtToken=${token}&customer=${customerId}`;
        } else {
            // If no token, try direct navigation (might use session cookies)
            window.location.href = `${apiServer}/api/integrations/fiken/request-access?customer=${customerId}`;
        }
    }

    /**
     * Disconnect/revoke OAuth connection
     * @param {number} customerId - The customer ID to disconnect
     */
    async disconnectOAuth(customerId) {
        const response = await util.fetchApi('/api/integrations/fiken/revoke', {
            method: 'POST',
        }, {
            customer: customerId,
        });

        if (response.status === 200) {
            console.log('Fiken disconnected successfully');
            // Reload user info to get updated customer data BEFORE updating UI state
            const userStore = require('./user').default;
            await userStore.getInfo();
        }

        runInAction(() => {
            switch (response.status) {
                case 200:
                    this.setOAuthStatus('disconnected');
                    PubSub.publish(topics.NOTIFY, {
                        type: 'success',
                        message: 'Fiken disconnected successfully',
                    });
                    break;
                default:
                    console.error('fikenStore.disconnectOAuth', response);
                    this.setOAuthError(response.message || 'Failed to disconnect Fiken');
                    PubSub.publish(topics.NOTIFY, {
                        type: 'error',
                        message: response.message || 'Failed to disconnect Fiken',
                    });
                    break;
            }
        });

        return response;
    }

    /**
     * Check OAuth callback status
     * Call this when page loads with success/error params
     */
    @action
    async checkOAuthCallback() {
        const urlParams = new URLSearchParams(window.location.search);
        const hash = window.location.hash;

        // Check both URL and hash for parameters
        const hashParams = new URLSearchParams(hash.includes('?') ? hash.split('?')[1] : '');

        const success = urlParams.get('success') || hashParams.get('success');
        const error = urlParams.get('error') || hashParams.get('error');
        const message = urlParams.get('message') || hashParams.get('message');

        if (success === 'true') {
            console.log('Fiken OAuth callback: success');
            // Reload user info to get updated customer data with OAuth token
            const userStore = require('./user').default;
            await userStore.getInfo();

            runInAction(() => {
                this.setOAuthStatus('connected');
                PubSub.publish(topics.NOTIFY, {
                    type: 'success',
                    message: 'Fiken connected successfully! You can now use Fiken integration.',
                });
            });
            // Clean up URL
            this.cleanupOAuthUrl();
        } else if (error) {
            runInAction(() => {
                this.setOAuthError(decodeURIComponent(message || error));
                PubSub.publish(topics.NOTIFY, {
                    type: 'error',
                    message: `Failed to connect Fiken: ${decodeURIComponent(message || error)}`,
                });
            });
            // Clean up URL
            this.cleanupOAuthUrl();
        }
    }

    /**
     * Clean up OAuth parameters from URL
     */
    cleanupOAuthUrl() {
        const url = new URL(window.location.href);
        url.searchParams.delete('success');
        url.searchParams.delete('error');
        url.searchParams.delete('message');

        // Also clean hash params
        let hash = url.hash;
        if (hash.includes('?')) {
            const [hashPath, hashQuery] = hash.split('?');
            const hashParams = new URLSearchParams(hashQuery);
            hashParams.delete('success');
            hashParams.delete('error');
            hashParams.delete('message');
            const cleanHashQuery = hashParams.toString();
            url.hash = cleanHashQuery ? `${hashPath}?${cleanHashQuery}` : hashPath;
        }

        window.history.replaceState({}, '', url.toString());
    }

    async getUser() {
        const response = await util.fetchApi(`/api/integrations/fiken/user`);
        switch (response.status) {
            case 200:
                // console.log('fikenStore.getPaymentStatus', response);
                return response;
            default:
                console.error('fikenStore.getUsers', response);
                return response;
        }
    }

    async getCompanies() {
        const response = await util.fetchApi(`/api/integrations/fiken/companies`);
        switch (response.status) {
            case 200:
                // console.log('fikenStore.getPaymentStatus', response);
                return response;
            default:
                console.error('fikenStore.getCompanies', response);
                return response;
        }
    }

    @observable bankAccounts = [];

    async getBankAccounts(slug) {
        if (!slug) {
            return;
        }
        const response = await util.fetchApi(`/api/integrations/fiken/companies/${slug}/bankAccounts`);
        switch (response.status) {
            case 200:
                this.updateKeyValue('bankAccounts', response.data || []);
                return response;
            default:
                console.error('fikenStore.getBankAccounts', response);
                return response;
        }
    }

    async getAccounts() {
        const response = await util.fetchApi(`/api/integrations/fiken/companies/${this.slug}/accounts`);
        switch (response.status) {
            case 200:
                // console.log('fikenStore.getPaymentStatus', response);
                return response;
            default:
                console.error('fikenStore.getAccounts', response);
                return response;
        }
    }

    async getCompanyContacts() {
        if (!this.slug) {
            return;
        }
        const response = await util.fetchApi(`/api/integrations/fiken/companies/${this.slug}/contacts`);
        switch (response.status) {
            case 200:
                // console.log('fikenStore.getPaymentStatus', response);
                return response;
            default:
                console.error('fikenStore.getCompanyContacts', response);
                return response;
        }
    }

    async createCompanyContact() {
        if (!this.slug) {
            return;
        }
        const response = await util.fetchApi(`/api/integrations/fiken/companies/${this.slug}/contacts`, {
            method: 'POST',
        }, {
            "name": "Fiken AS",
            "email": "kontakt@fiken.gmail",
            "organizationNumber": "913312465",
            "phoneNumber": "62158537",
            "memberNumber": 123456789,
            "customer": true,
            "supplier": false,
            "bankAccountNumber": "11112233334",
            "contactPerson": [
                {
                    "name": "Betty Boop",
                    "email": "bb@gmail.com",
                    "phoneNumber": "98573564",
                    "address": {
                        "streetAddress": "Karl Johan 34",
                        "streetAddressLine2": "H0405",
                        "city": "Oslo",
                        "postCode": "0550",
                        "country": "Norway"
                    }
                }
            ],
            "currency": "NOK",
            "language": "Norwegian",
            "inactive": false,
            "daysUntilInvoicingDueDate": 15,
            "address": {
                "streetAddress": "Karl Johan 34",
                "streetAddressLine2": "H0405",
                "city": "Oslo",
                "postCode": "0550",
                "country": "Norway"
            },
            // "groups": [
            //     "string"
            // ]
        });
        switch (response.status) {
            case 200:
                // console.log('fikenStore.createCompanyContact', response);
                return response;
            default:
                console.error('fikenStore.createCompanyContact', response);
                return response;
        }
    }


    async getCompanyContactsContactPerson() {
        if (!this.slug || !this.contactId) {
            return;
        }
        const response = await util.fetchApi(`/api/integrations/fiken/companies/${this.slug}/contacts/${this.contactId}/contactPerson`);
        switch (response.status) {
            case 200:
                // console.log('fikenStore.getPaymentStatus', response);
                return response;
            default:
                console.error('fikenStore.getCompanyContacts', response);
                return response;
        }
    }

    async getCompanyInvoices() {
        if (!this.slug) {
            return;
        }
        const response = await util.fetchApi(`/api/integrations/fiken/companies/${this.slug}/invoices`);
        switch (response.status) {
            case 200:
                // console.log('fikenStore.getPaymentStatus', response);
                return response;
            default:
                console.error('fikenStore.getCompanyInvoices', response);
                return response;
        }
    }

    async createCompanyInvoice(saleId, { force = false } = {}) {
        if (!this.slug) {
            return;
        }
        if (saleId) {
            const path = `/api/integrations/fiken/companies/${this.slug}/invoices/${saleId}${force ? '?force=true' : ''}`;
            const response = await util.fetchApi(path, {
                method: 'POST',
            }, {});
            switch (response.status) {
                case 200:
                    // console.log('fikenStore.getPaymentStatus', response);
                    return response;
                default:
                    console.error('fikenStore.createCompanyInvoice', response);
                    return response;
            }
        }

        const response = await util.fetchApi(`/api/integrations/fiken/companies/${this.slug}/invoices`, {
            method: 'POST',
        }, {
            "uuid": "e15c445c-30f7-4c4f-bd13-c8ff6aeb3ad4",
            "issueDate": "2024-09-30",
            "dueDate": "2024-10-15",
            "lines": [
                {
                    "netAmount": 4500,
                    "vatType": "HIGH",
                    "quantity": 5,
                    "unitPrice": 1200,
                    "discountPercent": 25,
                    "productName": "Gardening Gloves VI2",
                    "description": "Goatskin, with extra-long suede cuffs",
                    "comment": "One size fits all",
                    "incomeAccount": "3000"
                }
            ],
            "ourReference": "Øistein Sørensen",
            "yourReference": "Kari Nordmann",
            "orderReference": "123456",
            "customerId": this.contactId,
            "contactPersonId": this.contactPersonId,
            "bankAccountCode": "1920:10001",
            "currency": "NOK",
            "invoiceText": "Invoice for services rendered during the Oslo Knitting Festival.",
            "cash": false,
        });
        switch (response.status) {
            case 200:
                // console.log('fikenStore.getPaymentStatus', response);
                return response;
            default:
                console.error('fikenStore.createCompanyInvoice', response);
                return response;
        }
    }

    async sendCompanyInvoice(saleId) {
        if (!this.slug || !saleId) {
            return { status: 400, message: 'Missing Fiken slug or saleId.' };
        }
        const response = await util.fetchApi(`/api/integrations/fiken/companies/${this.slug}/invoices/send/${saleId}`, {
            method: 'POST',
        }, {});
        switch (response.status) {
            case 200:
                return response;
            default:
                console.error('fikenStore.sendCompanyInvoice', response);
                return response;
        }
    }

    async getCompanySales() {
        if (!this.slug) {
            return;
        }
        const response = await util.fetchApi(`/api/integrations/fiken/companies/${this.slug}/sales`);
        switch (response.status) {
            case 202:
                // console.log('fikenStore.getPaymentStatus', response);
                return response;
            default:
                console.error('fikenStore.getCompanySales', response);
                return response;
        }
    }

    async updateInvoicesByDate(cutoffDate, cutoffDateEnd) {
        console.log('fikenStore.updateInvoicesByDate called', this.slug);
        if (!this.slug) {
            return;
        }
        const response = await util.fetchApi(`/api/integrations/fiken/companies/${this.slug}/sales`, {
            method: 'PATCH',
        }, {
            "invoiceDateFrom": cutoffDate,
            "invoiceDateTo": cutoffDateEnd
        });
        switch (response.status) {
            case 202:
                // console.log('fikenStore.getPaymentStatus', response);
                return response;
            default:
                console.error('fikenStore.updateInvoicesByDate', response);
                return response;
        }
    }



    async simulateSale(saleId) {
        if (!this.slug || !saleId) {
            return;
        }
        const response = await util.fetchApi(`/api/integrations/fiken/companies/${this.slug}/sales/${saleId}?simulate=true`, {
            method: 'POST',
        }, {});
        switch (response.status) {
            case 200:
                return response;
            default:
                console.error('fikenStore.simulateSale', response);
                return response;
        }
    }

    async simulateInvoice(saleId) {
        if (!this.slug || !saleId) {
            return;
        }
        const response = await util.fetchApi(`/api/integrations/fiken/companies/${this.slug}/invoices/${saleId}?simulate=true`, {
            method: 'POST',
        }, {});
        switch (response.status) {
            case 200:
                return response;
            default:
                console.error('fikenStore.simulateInvoice', response);
                return response;
        }
    }

    async createCompanySale(saleId) {
        if (!this.slug) {
            return;
        }
        if (saleId) {
            const response = await util.fetchApi(`/api/integrations/fiken/companies/${this.slug}/sales/${saleId}`, {
                method: 'POST',
            }, {});
            switch (response.status) {
                case 200:
                    // console.log('fikenStore.getPaymentStatus', response);
                    return response;
                default:
                    console.error('fikenStore.createCompanySale', response);
                    return response;
            }
        }

        const response = await util.fetchApi(`/api/integrations/fiken/companies/${this.slug}/sales`, {
            method: 'POST',
        }, {
            "saleNumber": "XK455L",
            "date": "2024-10-01",
            "kind": "external_invoice",
            "totalPaid": 5625,
            "lines": [
                {
                    "description": "sale of goods",
                    "netPrice": 4500,
                    "vat": 1125,
                    "vatType": "HIGH",
                    "account": "3000",
                }
            ],
            "customerId": this.contactId,
            "currency": "NOK",
            "dueDate": "2024-10-10",
            "kid": "5855454756",
            "paymentAccount": "1920:10001",
            "paymentDate": "2024-10-01",
            // "paymentFee": 634550,
            // "projectId": 2888156

        });
        switch (response.status) {
            case 200:
                // console.log('fikenStore.getPaymentStatus', response);
                return response;
            default:
                console.error('fikenStore.createCompanySale', response);
                return response;
        }
    }
}

const store = new FikenStore();
export default store;
